import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEdit, FaEye, FaPowerOff } from "react-icons/fa";
import Grid from "../components/Grid";
import { toast } from "react-toastify";
import { DataGrid } from "@mui/x-data-grid";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Clusters({ onAddClusterClick }) {
  const [clusters, setClusters] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [viewCluster, setViewCluster] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort] = useState({ field: "cluster_name", direction: "asc" });
  const [selectedSearchField, setSelectedSearchField] = useState("cluster_name");

  // Get user role from localStorage (or replace with your auth context if needed)
  const userRole = localStorage.getItem("role") || "";

  // Fetch organizations from API and return array
  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.organizations || [];
    } catch (err) {
      toast.error("Error fetching organizations");
      return [];
    }
  };

  // Fetch organizations with locations from API and return array
  const fetchOrganizationLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please login.");
        return [];
      }

      const response = await axios.get(
        `${API_BASE_URL}/organizations/organizationLocations`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.organizations || [];
    } catch (error) {
      console.error("Error fetching organization locations:", error);
      toast.error("Error fetching organization locations.");
      return [];
    }
  };

  // Fetch clusters list
  const fetchClusters = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/clusters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClusters(res.data.map((c) => ({ ...c, id: c._id })));
    } catch (err) {
      // toast.error("Error fetching clusters");
    }
  };

  const fetchOrgClusters = async () => {  
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/clusters/orgClusters/myClusters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClusters(res.data.map((c) => ({ ...c, id: c._id })));
    } catch (err) {
      // toast.error("Error fetching clusters");
    }
  };
  useEffect(() => {
    if (userRole === "Main Admin") {
      fetchClusters();
    } else {
      fetchOrgClusters();
    }
  }, [userRole]);


  // Merge organizations and locations by _id and setOrganizations
  const fetchAndMergeData = async () => {
    const orgList = await fetchOrganizations();
    const orgLocs = await fetchOrganizationLocations();

    const merged = orgList.map((org) => {
      const locObj = orgLocs.find((loc) => loc._id === org._id);
      return {
        ...org,
        locations: locObj?.locations || [],
      };
    });

    setOrganizations(merged);
  };

  // On mount, fetch orgs+locations and clusters
  useEffect(() => {
    fetchAndMergeData();
    fetchClusters();
    fetchOrgClusters();
  }, []);

  // When user clicks edit icon for cluster
  const handleClusterClick = (cluster) => {
    setSelectedCluster({
      ...cluster,
      cluster_name: cluster.cluster_name || "",
      cluster_description: cluster.cluster_description || "",
      organizations: cluster.organizations?.map((org) => (typeof org === "object" ? org._id : org)) || [],
    });
  };

const handleViewCluster = async (cluster) => {
  try {
    const token = localStorage.getItem("token");

    const res = await axios.get(
      `${API_BASE_URL}/clusters/${cluster._id}/invoiceViewFlags`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const flags = res.data?.flags || {}; // { [orgId]: true/false }

    const orgsWithFlags = (cluster.organizations || []).map((org) => {
      const orgId = typeof org === "object" ? org._id : org;
      return {
        _id: orgId,
        invoice_view: !!flags[orgId],
      };
    });

    setViewCluster({ ...cluster, organizations: orgsWithFlags });
  } catch (err) {
    console.error("Error fetching invoice flags:", err);
    toast.error("Failed to load invoice view settings");
  }
};



  // Toggle cluster active/inactive status
  const handleToggleActiveStatus = async (cluster) => {
    try {
      const token = localStorage.getItem("token");
      const updatedStatus = cluster.status === "active" ? "inactive" : "active";

      await axios.put(
        `${API_BASE_URL}/clusters/${cluster._id}/status`,
        { status: updatedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Cluster marked as ${updatedStatus}`);
      fetchClusters();
    } catch (err) {
      toast.error("Error updating status");
    }
  };

  const handleClosePopup = () => setSelectedCluster(null);

  const handleUpdateCluster = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/clusters/update/${selectedCluster._id}`,
        {
          cluster_name: selectedCluster.cluster_name,
          cluster_description: selectedCluster.cluster_description,
          organizations: selectedCluster.organizations,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cluster updated successfully");
      setSelectedCluster(null);
      fetchClusters();
    } catch (err) {
      toast.error("Error updating cluster");
    }
  };

  const handleDeleteCluster = async () => {
    if (!window.confirm("Are you sure you want to delete this cluster? This action cannot be undone.")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_BASE_URL}/clusters/delete/${selectedCluster._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cluster deleted successfully");
      setSelectedCluster(null);
      fetchClusters();
    } catch (err) {
      const errMsg = err.response?.data?.message || "Error deleting cluster";
      toast.error(errMsg);
    }
  };

  // Filter and sort clusters for display in grid
  const filteredClusters = clusters
    .filter((cluster) => {
      const val = searchTerm.toLowerCase();
      switch (selectedSearchField) {
        case "cluster_code":
          return cluster.cluster_code?.toLowerCase().includes(val);
        case "cluster_name":
          return cluster.cluster_name?.toLowerCase().includes(val);
        case "cluster_description":
          return cluster.cluster_description?.toLowerCase().includes(val);
        case "organizations":
          return cluster.organizations?.some((org) =>
            (typeof org === "object" ? org.organization_name : org)?.toLowerCase().includes(val)
          );
        case "status":
          return cluster.status?.toLowerCase().includes(val);
        default:
          return false;
      }
    })
    .sort((a, b) => {
      if (a[sort.field] < b[sort.field]) return sort.direction === "asc" ? -1 : 1;
      if (a[sort.field] > b[sort.field]) return sort.direction === "asc" ? 1 : -1;
      return 0;
    });

  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredClusters : clusters;

    if (dataToExport.length === 0) {
      toast.warn("No location data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Locations");

    const headerRow = [
      "Cluster Code",
      "Cluster Name",
      "Description",
      "Organizations",
      "Status",
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" }, // Light yellow
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((location) => {
      worksheet.addRow([
        location.cluster_code,
        location.cluster_name,
        location.cluster_description || "No description",
        location.organizations
          ? location.organizations.map((org) => (typeof org === "object" ? org.organization_name : org)).join(", ")
          : "—",
        location.status
      ]);
    });

    worksheet.columns.forEach((column, index) => {
      const headerText = headerRow[index];
      const maxLength = Math.max(
        headerText.length,
        ...dataToExport.map((row) =>
          (typeof row === "string"
            ? row
            : row?.[headerText.toLowerCase().replace(/ /g, "_")] || ""
          ).toString().length
        )
      );
      column.width = maxLength < 20 ? 20 : maxLength;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "clusters.xlsx");
  };
  
  const handleInvoiceViewToggle = async (orgId, newValue) => {
    try {
      const token = localStorage.getItem("token");

      await axios.put(
        `${API_BASE_URL}/clusters/${viewCluster._id}/organizations/${orgId}/invoiceView`,
        { invoice_view: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setViewCluster((prev) => ({
        ...prev,
        organizations: prev.organizations.map((org) =>
          org._id === orgId ? { ...org, invoice_view: newValue } : org
        ),
      }));

      toast.success("Invoice view updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update invoice view");
    }
  };

  // Grid columns config
  const columns = [
    { field: "cluster_code", headerName: "Code", flex: 1 },
    { field: "cluster_name", headerName: "Name", flex: 1 },
    { field: "cluster_description", headerName: "Description", flex: 2 },
    {
      field: "organizations",
      headerName: "Organizations",
      flex: 2,
      renderCell: (params) =>
        params.row.organizations?.map((org) => (typeof org === "object" ? org.organization_name : org)).join(", ") || "—",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => {
        const isActive = params.row.status === "active";
        return (
          <span
            className={`px-3 py-1 rounded-full text-white font-semibold text-sm ${
              isActive ? "bg-green-500" : "bg-gray-400"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => {
        const isActive = params.row.status === "active";

        return (
          <div className="flex items-center gap-2 w-full h-full">
            <button
              onClick={() => handleViewCluster(params.row)}
              className="bg-yellow-500 text-white p-2 flex items-center"
              style={{ width: "30px", height: "30px" }}
              title="View"
            >
              <FaEye />
            </button>
            {userRole === "Main Admin" ? (
              <>
                <button
                  onClick={() => handleClusterClick(params.row)}
                  className="bg-blue-500 text-white p-2 flex items-center"
                  style={{ width: "30px", height: "30px" }}
                  title="Edit"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleToggleActiveStatus(params.row)}
                  className={`p-2 flex items-center ${
                    isActive ? "bg-red-500 text-white" : "text-gray-600"
                  }`}
                  style={{ width: "30px", height: "30px" }}
                  title={isActive ? "Deactivate Cluster" : "Activate Cluster"}
                >
                  <FaPowerOff />
                </button>
              </>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-semibold">Clusters</h1>
          {userRole === "Main Admin" ? (
           
          <button
            onClick={onAddClusterClick}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Add Cluster
          </button>
          ) : null}
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="cluster_name">Cluster Name</option>
                <option value="cluster_code">Cluster Code</option>
                <option value="cluster_description">Description</option>
                <option value="organizations">Organizations</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Clusters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>         

          <Grid rows={filteredClusters} columns={columns} getRowId={(row) => row.id} />
        </div>
      {viewCluster && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
          style={{ overflowY: "auto" }}
        >
          <div
            className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <button
              onClick={() => setViewCluster(null)}
              className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              title="Close"
            >
              &times;
            </button>

            <h2 className="text-xl font-semibold mb-4">
              Cluster Details: {viewCluster.cluster_name}
            </h2>

            <div className="mb-4">
              <p>
                <strong className="text-sm font-semibold">Cluster Code:</strong>{" "}
                {viewCluster.cluster_code}
              </p>
              <p>
                <strong className="text-sm font-semibold">Description:</strong>{" "}
                {viewCluster.cluster_description || "No description provided"}
              </p>
              <p>
                <strong className="text-sm font-semibold">Status:</strong>{" "}
                {viewCluster.status}
              </p>
            </div>

            <h3 className="text-lg font-semibold mt-4 mb-2">Organizations</h3>
            <div style={{ width: "100%" }}>
              <DataGrid
                autoHeight
                rows={(viewCluster.organizations || []).map((org, index) => {
                  const orgObj = organizations.find((o) => o._id === org._id) || {};
                  return {
                    id: org._id || index,
                    organization_name: orgObj.organization_name || "N/A",
                    organization_code: orgObj.organization_code || "N/A",
                    locations: Array.isArray(orgObj.locations)
                      ? orgObj.locations.map((loc) => loc.location_name).join(", ")
                      : "No locations",
                    invoice_view: org.invoice_view || false, // ✅ comes from backend flags
                  };
                })}
                columns={[
                  {
                    field: "invoice_view",
                    headerName: "Invoice View",
                    flex: 0.5,
                    minWidth: 130,
                    renderCell: (params) => (
                      <input
                        type="checkbox"
                        checked={params.row.invoice_view || false}
                        onChange={(e) => handleInvoiceViewToggle(params.row.id, e.target.checked)}
                      />
                    ),
                  },
                  { field: "organization_name", headerName: "Organization Name", flex: 1, minWidth: 150 },
                  { field: "organization_code", headerName: "Organization Code", flex: 1, minWidth: 130 },
                  { field: "locations", headerName: "Locations", flex: 2, minWidth: 200 },
                ]}
                pageSize={5}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
                hideFooterSelectedRowCount
              />
            </div>
          </div>
        </div>
      )}


        {selectedCluster && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-screen overflow-y-auto">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
                title="Close"
              >
                &times;
              </button>

              <div className="flex flex-col gap-4">
                <h2 className="text-xl font-semibold mb-4">Edit Cluster</h2>

                <input
                  type="text"
                  placeholder="Cluster Name"
                  value={selectedCluster.cluster_name}
                  onChange={(e) =>
                    setSelectedCluster({ ...selectedCluster, cluster_name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />

                <textarea
                  placeholder="Cluster Description"
                  value={selectedCluster.cluster_description}
                  onChange={(e) =>
                    setSelectedCluster({
                      ...selectedCluster,
                      cluster_description: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />

                <label className="text-gray-700 mb-2 block">Select Organization</label>
                <select
                  value=""
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (!selectedId) return;

                    if (selectedCluster.organizations.includes(selectedId)) {
                      toast.warning("This organization is already selected.");
                    } else {
                      setSelectedCluster((prev) => ({
                        ...prev,
                        organizations: [...prev.organizations, selectedId],
                      }));
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">-- Select an Organization --</option>
                  {organizations.map((org) => (
                    <option key={org._id} value={org._id}>
                      {org.organization_name}
                    </option>
                  ))}
                </select>

                {/* Organizations Table Header */}
                <div className="mt-4">
                  <label className="block text-gray-700 mb-2 text-lg font-semibold">
                    Organizations
                  </label>

                  <div className="grid grid-cols-12 gap-4 font-semibold text-gray-800 mb-2">
                    <div className="col-span-4">Select Organization</div>
                    <div className="col-span-4">Locations</div>
                    <div className="col-span-2">Organization Code</div>
                    <div className="col-span-1">Action</div>
                  </div>

                  {/* Organization Rows */}
                  {selectedCluster.organizations.map((orgId, index) => {
                    const selectedOrg = organizations.find((org) => org._id === orgId);
                    const locationsText =
                      selectedOrg?.locations?.map((loc) => loc.location_name).join(", ") ||
                      "No locations available";

                    return (
                      <div
                        key={orgId + index}
                        className="grid grid-cols-12 gap-4 items-center mb-4"
                      >
                        {/* Dropdown */}
                        <div className="col-span-4">
                          <select
                            className="w-full p-2 border rounded text-gray-900"
                            value={orgId}
                            onChange={(e) => {
                              const newId = e.target.value;
                              if (
                                selectedCluster.organizations.includes(newId) &&
                                newId !== orgId
                              ) {
                                toast.warning("Organization already selected");
                                return;
                              }

                              const newOrgs = [...selectedCluster.organizations];
                              newOrgs[index] = newId;
                              setSelectedCluster({ ...selectedCluster, organizations: newOrgs });
                            }}
                          >
                            <option value="" disabled>
                              --- Select Organization ---
                            </option>
                            {organizations.map((org) => (
                              <option
                                key={org._id}
                                value={org._id}
                                disabled={
                                  selectedCluster.organizations.includes(org._id) &&
                                  org._id !== orgId
                                }
                              >
                                {org.organization_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Locations */}
                        <div className="col-span-4 text-gray-700 text-sm">{locationsText}</div>

                        {/* Code */}
                        <div className="col-span-2 text-gray-700 text-sm">
                          {selectedOrg?.organization_code || ""}
                        </div>

                        {/* Remove Button */}
                        <div className="col-span-1 text-right">
                          <button
                            onClick={() => {
                              if (selectedCluster.organizations.length === 1) {
                                toast.error("At least one organization required.");
                                return;
                              }
                              setSelectedCluster((prev) => ({
                                ...prev,
                                organizations: prev.organizations.filter((_, i) => i !== index),
                              }));
                            }}
                            className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-700"
                            title="Remove"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Organization Button */}
                  <div className="mt-2">
                    <button
                      onClick={() =>
                        setSelectedCluster((prev) => ({
                          ...prev,
                          organizations: [...prev.organizations, ""],
                        }))
                      }
                      className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800"
                    >
                      Add Organization
                    </button>
                  </div>
                </div>
                {/* Buttons Row */}
                <div className="flex justify-between mt-4">
                  {/* Delete Button */}
                  <button
                    onClick={handleDeleteCluster}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg"
                  >
                    Delete
                  </button>

                  {/* Update Button */}
                  <button
                    onClick={handleUpdateCluster}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Clusters;
